
import { BaseNotification } from '@/types/notifications';
import { ConfigManager } from '../core/ConfigManager';
import { NotificationDeliveryService } from './NotificationDeliveryService';
import { UserRoutingService } from '../routing/UserRoutingService';
import { getNotificationTemplate, NotificationTemplateType } from "../templates";

export class NotificationDeliveryManager {
  private configManager: ConfigManager;
  private deliveryService: NotificationDeliveryService;
  private routingService: UserRoutingService;

  constructor() {
    this.configManager = new ConfigManager();
    this.deliveryService = new NotificationDeliveryService();
    this.routingService = new UserRoutingService();
  }

  public async deliverNotification(notification: any, specificUserId?: string): Promise<void> {
    try {
      const category = notification.category || "system";
      const config = this.configManager.getConfig(category);
      const templateObj = getNotificationTemplate(category as NotificationTemplateType);

      let subject, message;
      if (templateObj) {
        subject = templateObj.subject.replace(/\{\{(\w+)\}\}/g, (_, key) => notification[key] ?? "");
        message = templateObj.body.replace(/\{\{(\w+)\}\}/g, (_, key) => notification[key] ?? "");
      } else {
        subject = notification.title;
        message = notification.message;
      }
      notification.title = subject;
      notification.message = message;

      if (
        config.escalationLevels !== undefined &&
        config.currentEscalationLevel !== undefined &&
        config.currentEscalationLevel >= (config.escalationLevels || 3)
      ) {
        console.info('Max escalation reached for', category);
        return;
      }

      const recipients = this.routingService.getRecipients(notification, specificUserId);

      for (const recipient of recipients) {
        const contactInfo = this.routingService.getContactInfo(recipient.userId);
        const templateChannels = templateObj?.channels || [];
        
        if (config.pushEnabled && recipient.channels.push && templateChannels.includes("push")) {
          await this.deliveryService.sendPushNotification(notification, {
            priority: notification.priority,
            category: notification.category
          });
        }
        
        if (config.emailEnabled && recipient.channels.email && contactInfo.email && templateChannels.includes("email")) {
          await this.deliveryService.sendEmailNotification(notification, contactInfo.email, {
            subject,
            priority: notification.priority,
            category: notification.category
          });
        }
        
        if (config.smsEnabled && recipient.channels.sms && contactInfo.phone && templateChannels.includes("sms")) {
          await this.deliveryService.sendSmsNotification(notification, contactInfo.phone, {
            priority: notification.priority,
            category: notification.category
          });
        }
      }
      
      this.updateReminderScheduling(notification);
    } catch (error) {
      console.error('Error delivering notification:', error);
    }
  }

  private updateReminderScheduling(notification: any): void {
    try {
      const config = this.configManager.getConfig(notification.category);
      if (!config.reminderFrequency || config.reminderFrequency === 'none') {
        return;
      }
      const updatedConfig = {
        ...config,
        lastReminderSent: new Date().toISOString(),
        currentEscalationLevel: (config.currentEscalationLevel || 0) + 1
      };
      this.configManager.setConfig(updatedConfig, notification.category);
      if (updatedConfig.currentEscalationLevel < (updatedConfig.escalationLevels || 3)) {
        console.log(
          `Next reminder for notification ${notification.id} will be sent in ${
            updatedConfig.reminderFrequency === 'daily' ? '24 hours' : '7 days'
          }`
        );
      }
    } catch (error) {
      console.error('Error updating reminder scheduling:', error);
    }
  }
}
